import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useDeviceInfo } from './DeviceInfoContext'
import { createSubmission } from '../../lib/useSubmission'
import FeedbackSheet from '../feedback/FeedbackSheet'

const FeedbackContext = createContext(undefined)

export const FeedbackProvider = ({ children }) => {
  const { deviceInfo } = useDeviceInfo()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [defaultReason, setDefaultReason] = useState(null)
  const [hideReasonSelector, setHideReasonSelector] = useState(false)
  const [additionalData, setAdditionalData] = useState(null)

  const openFeedback = (reason, additionalData, hideSelector) => {
    setIsSheetOpen(true)
    if (reason) {
      setDefaultReason(reason)
    }
    if (additionalData) {
      setAdditionalData(additionalData)
    }
    setHideReasonSelector(hideSelector)
  }

  const closeFeedback = useCallback(() => {
    setIsSheetOpen(false)
  }, [])

  const submitFeedback = useCallback(
    async (option, text, image) => {
      const isContentReport = option || defaultReason === 'content_report'
      const submissionData = {
        body: text,
        reason: option || defaultReason,
        image,
        metadata: {
          ...deviceInfo,
          ...(window?.location?.href ? { url: window.location.href } : {}),
        },
      }

      if (
        isContentReport &&
        additionalData?.reportable_type &&
        additionalData?.reportable_id
      ) {
        submissionData.reportable_type = additionalData.reportable_type
        submissionData.reportable_id = additionalData.reportable_id
      }

      const resp = await createSubmission(submissionData)

      if (!resp.success) {
        throw new Error('Failed to submit feedback')
      }

      return resp
    },
    [createSubmission, deviceInfo, additionalData, defaultReason]
  )

  const value = useMemo(
    () => ({
      openFeedback,
      closeFeedback,
      submitFeedback,
      isSheetOpen,
    }),
    [closeFeedback, isSheetOpen, submitFeedback]
  )

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackSheet
        isSheetOpen={isSheetOpen}
        onClose={closeFeedback}
        defaultReason={defaultReason}
        hideReasonSelector={hideReasonSelector}
        onSubmitFeedback={submitFeedback}
      />
    </FeedbackContext.Provider>
  )
}

export const useFeedback = () => {
  const context = useContext(FeedbackContext)
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackProvider')
  }
  return context
}
